'use client'
import {
  comparePageRouteSpecificity,
  isClientMetricsEnabled,
  matchesPageRoute,
  type PageReadinessState,
} from '@brew/observability/browser'
import { useEffect, useRef } from 'react'
import { PAGE_CONTENT_CONTRACTS } from '@/lib/observability/page-content-contracts.runtime.generated'
import { pageContentReadiness } from '@/lib/observability/page-content-readiness'

type Key = keyof typeof PAGE_CONTENT_CONTRACTS
type Lease = ReturnType<typeof pageContentReadiness.attach>
/** Observe committed foreground gates without adding a Next URL read to a prerendered tree. */
export function usePageContentReadiness(
  pageKey: Key | ReadonlyArray<Key>,
  state: PageReadinessState,
  options: { part?: string; scope?: string; enabled?: boolean } = {}
): void {
  const current = useRef<{ identity: string; lease: Lease } | null>(null)
  useEffect(() => {
    if (!isClientMetricsEnabled() || options.enabled === false) {
      current.current?.lease.release()
      current.current = null
      return
    }
    try {
      const { pathname, search } = window.location
      const keys = typeof pageKey === 'string' ? [pageKey] : pageKey
      const key = keys
        .filter((candidate) =>
          matchesPageRoute(PAGE_CONTENT_CONTRACTS[candidate].route, pathname)
        )
        .sort((left, right) =>
          comparePageRouteSpecificity(
            PAGE_CONTENT_CONTRACTS[left].route,
            PAGE_CONTENT_CONTRACTS[right].route
          )
        )[0]
      if (!key) {
        current.current?.lease.release()
        current.current = null
        return
      }
      const part = options.part ?? 'foreground'
      const identity = JSON.stringify([
        key,
        part,
        pathname,
        search,
        options.scope,
      ])
      if (current.current?.identity !== identity) {
        current.current?.lease.release()
        current.current = {
          identity,
          lease: pageContentReadiness.attach({
            surface: key,
            part,
            pathname,
            search,
            scope: options.scope,
          }),
        }
      }
      current.current.lease.update(state)
    } catch {
      /* A location read cannot affect product rendering. */
    }
  })
  useEffect(
    () => () => {
      current.current?.lease.release()
      current.current = null
    },
    []
  )
}
